import { Component, DestroyRef, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { ServicesService } from 'src/services/services.service';
import { ProductQrScanAnalyticsComponent } from './product-qr-scan-analytics.component';
import jsPDF from 'jspdf';
import * as QRCode from 'qrcode';
import { Location } from '@angular/common';

export interface ProductQrBatchRow {
  srNo: number;
  batch_id: any;
  batch_name: string;
  product_title: string;
  company_name?: string;
  total_qr_count: number;
  points_per_scan: number;
  assigned_user: string;
  expiry_date: string;
  status: string;
  claimed_count: number;
  active_count: number;
  action: any;
}

@Component({
  selector: 'app-product-qr',
  templateUrl: './product-qr.component.html',
  styleUrls: ['./product-qr.component.scss'],
  standalone: false
})

export class ProductQrComponent {
  displayedColumns: string[] = [
    'srNo',
    'batch_name',
    'product_title',
    'total_qr_count',
    'points_per_scan',
    'assigned_user',
    'expiry_date',
    'claimed_count',
    'status',
    'action',
  ];

  dataSource = new MatTableDataSource<ProductQrBatchRow>();
  private destroyRef = inject(DestroyRef);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  so_id: any;
  batches: ProductQrBatchRow[] = [];

  constructor(
    private service: ServicesService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private location: Location
  ) { }

  ngOnInit() {
    this.so_id = this.route.snapshot.paramMap.get('so_id');
    this.loadBatches();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  loadBatches() {
    this.spinner.show();
    this.service.get_product_qr_batches(this.so_id)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.spinner.hide()))
      .subscribe((res: any) => {
        const rows = res?.data || [];
        this.batches = rows.map((item: any, index: number) => ({
          srNo: index + 1,
          batch_id: item.batch_id,
          batch_name: item.batch_name,
          product_title: item.product_title,
          company_name: item.company_name || 'Our Stall',
          total_qr_count: Number(item.total_qr_count || item.total_qr || 0),
          points_per_scan: Number(item.points_per_scan || 0),
          assigned_user: item.assigned_user || '-',
          expiry_date: item.expiry_date || '-',
          status: item.is_active === 1 ? 'active' : 'inactive',
          claimed_count: Number(item.claimed_count || 0),
          active_count: Number(item.active_count || 0),
          action: item.batch_id,
        }));
        this.dataSource = new MatTableDataSource<ProductQrBatchRow>(this.batches);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      });
  }

  addBatch() {
    const dialogRef = this.dialog.open(AddProductQrBatchDialog, {
      width: '520px',
      disableClose: true,
      data: {
        so_id: this.so_id,
      }
    });

    dialogRef.afterClosed().subscribe((changed) => {
      if (changed) {
        this.loadBatches();
      }
    });
  }

  viewCodes(batch: ProductQrBatchRow) {
    this.dialog.open(ProductQrCodesDialog, {
      width: '95vw',
      maxWidth: '1100px',
      data: {
        batch_id: batch.batch_id,
        product_title: batch.product_title,
      }
    });
  }

  viewScanAnalytics(batch: ProductQrBatchRow) {
    this.dialog.open(ProductQrScanAnalyticsComponent, {
      width: '95vw',
      maxWidth: '1200px',
      data: {
        batch_id: batch.batch_id,
        product_title: batch.product_title,
      }
    });
  }

  viewClaimRewards(batch: ProductQrBatchRow) {
    this.dialog.open(ProductQrClaimComponent, {
      width: '95vw',
      maxWidth: '1100px',
      data: {
        batch_id: batch.batch_id,
        product_title: batch.product_title,
      }
    });
  }

  printStickers(batch: ProductQrBatchRow) {
    this.dialog.open(PrintStickersDialog, {
      width: '95vw',
      maxWidth: '95vw',
      data: {
        batch_id: batch.batch_id,
        product_title: batch.product_title,
        company_name: batch.company_name || 'Our Stall',
      }
    });
  }

  updateStatus(batch: ProductQrBatchRow, status: string) {
    this.service.update_product_qr_batch(batch.batch_id, status)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        if (res?.flag) {
          this.toastr.success(res.msg || 'Batch updated');
          this.loadBatches();
        } else {
          this.toastr.error(res?.msg || 'Batch update failed');
        }
      });
  }

  exportCodes(batch: ProductQrBatchRow) {
    this.service.export_product_qr_codes(batch.batch_id).subscribe((response: any) => {
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `product-qr-batch-${batch.batch_id}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  }

  back() {
    this.location.back();
  }
}

@Component({
  selector: 'add-product-qr-batch',
  templateUrl: './add-product-qr-batch.html',
  styleUrls: ['./product-qr.component.scss'],
  standalone: false
})
export class AddProductQrBatchDialog {
  BatchForm: FormGroup;
  products: any[] = [];
  stallUsers: any[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    private formBuilder: FormBuilder,
    private service: ServicesService,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<AddProductQrBatchDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.BatchForm = this.formBuilder.group({
      so_id: data.so_id,
      product_id: new FormControl('', [Validators.required]),
      assigned_user_id: new FormControl(0),
      batch_name: new FormControl('', [Validators.required]),
      quantity: new FormControl(50, [Validators.required, Validators.min(1), Validators.max(1000)]),
      points_per_scan: new FormControl(1, [Validators.required, Validators.min(1)]),
      expiry_date: new FormControl('', [Validators.required]),
      status: new FormControl('active', [Validators.required]),
    });
  }

  ngOnInit() {
    this.service.get_products(this.data.so_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        this.products = res?.data || [];

    });

    this.service.get_stall_users(this.data.so_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        this.stallUsers = res?.data || [];

    });
  }

  private getFieldLabel(field: string): string {
    const labels: { [key: string]: string } = {
      batch_name: 'Batch Name',
      product_id: 'Product',
      points: 'Points',
      total_qr: 'Total QR',
      expiry_date: 'Expiry Date'
    };

    return labels[field] || field;
  }

  onSubmit() {
    if (this.BatchForm.invalid) {
      this.BatchForm.markAllAsTouched();

      const firstInvalidKey = Object.keys(this.BatchForm.controls)
        .find(key => this.BatchForm.get(key)?.invalid);

      if (firstInvalidKey) {
        this.toastr.error(
          `${this.getFieldLabel(firstInvalidKey)} is required`
        );
      }

      return;
    }

    const value = { ...this.BatchForm.value };
    value.expiry_date = this.formatDate(value.expiry_date);

    this.service.add_product_qr_batch(value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        if (res?.flag) {
          this.toastr.success(res.msg || 'Batch created');
          this.dialogRef.close(true);
        } else {
          this.toastr.error(res?.msg || 'Batch creation failed');
        }
      });
  }

  private formatDate(date: any): string {
    if (!date) {
      return '';
    }
    if (typeof date === 'string') {
      return date;
    }
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }
}

@Component({
  selector: 'product-qr-codes',
  templateUrl: './product-qr-codes.html',
  styleUrls: ['./product-qr.component.scss'],
  standalone: false
})
export class ProductQrCodesDialog {
  displayedColumns = [
    'srNo',
    'serial_number',
    'batch_name',
    'user_name',
    'user_mobile',
    'points_awarded',
    'scan_count',
    'last_scanned_at',
    'device_name',
    'status'
  ];


  dataSource = new MatTableDataSource<any>();
  private destroyRef = inject(DestroyRef);

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private service: ServicesService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit() {
    this.service.get_product_qr_codes(this.data.batch_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        const rows = (res?.data || []).map((item: any, index: number) => ({
          ...item,
          srNo: index + 1,
        }));
        this.dataSource = new MatTableDataSource<any>(rows);
        this.dataSource.paginator = this.paginator;
      });
  }

  copy(url: string) {
    navigator.clipboard.writeText(url).then(() => {
      this.toastr.success('QR URL copied');
    });
  }

  getStatus(row: any): string {
    if (row.current_scan_count >= row.max_scans) {
      return 'Used';
    }

    if (row.expiry_date && new Date(row.expiry_date) < new Date()) {
      return 'Expired';
    }

    return 'Active';
  }
}

@Component({
  selector: 'app-product-qr-claim',
  templateUrl: './product-qr-claim.component.html',
  styleUrls: ['./product-qr.component.scss'],
  standalone: false
})
export class ProductQrClaimComponent {
  uid: any;
  loading = true;
  result: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ServicesService,
  ) { }

  ngOnInit() {
    this.uid = this.route.snapshot.paramMap.get('uid');
    const userId = sessionStorage.getItem('User_ID') || sessionStorage.getItem('user_id');
    const token = sessionStorage.getItem('session_token') || sessionStorage.getItem('token');

    if (!userId || !token) {
      sessionStorage.setItem('ProductQrReturnUrl', `/ProductQrClaim/${this.uid}`);
      this.router.navigate(['/admin-auth'], { queryParams: { returnUrl: `/ProductQrClaim/${this.uid}` } });
      return;
    }

    this.service.claim_product_qr(this.uid).subscribe({
      next: (res: any) => {
        this.result = res;
        this.loading = false;
      },
      error: () => {
        this.result = {
          flag: false,
          msg: 'Unable to claim this QR code right now.'
        };
        this.loading = false;
      }
    });
  }
}

@Component({
  selector: 'print-stickers-dialog',
  templateUrl: './print-stickers.html',
  styleUrls: ['./product-qr.component.scss'],
  standalone: false
})
export class PrintStickersDialog implements OnInit {
  loading = true;
  codes: any[] = [];
  layout = '3x8';
  offset = 0;

  constructor(
    private service: ServicesService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private dialogRef: MatDialogRef<PrintStickersDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { batch_id: any, product_title: string, company_name: string },
  ) { }

  ngOnInit() {
    this.loadCodes();
  }

  loadCodes() {
    this.loading = true;
    this.spinner.show();
    this.service.get_product_qr_hash_codes(this.data.batch_id).subscribe({
      next: async (res: any) => {
        const rows = res?.data || [];
        const generated = [];
        let index = 1;
        for (const item of rows) {
          try {
            const qrDataUrl = await QRCode.toDataURL(item.unique_hash || `https://aieventzo.com/scan-me/#/ProductQrClaim/${item.public_uid}`, {
              margin: 1,
              width: 200
            });
            generated.push({
              ...item,
              qrDataUrl
            });
            index++;
          } catch (e) {
            console.error('Failed to generate QR', e);
            generated.push({
              ...item,
            });
            index++;
          }
        }
        this.codes = generated;
        this.loading = false;
        this.spinner.hide();
      },
      error: () => {
        this.toastr.error('Failed to load QR codes');
        this.loading = false;
        this.spinner.hide();
      }
    });
  }

  getPreviewSlots() {
    const slots = [];
    for (let i = 0; i < this.offset; i++) {
      slots.push({ isEmpty: true });
    }
    for (const code of this.codes) {
      slots.push({ ...code, isEmpty: false });
    }
    return slots;
  }

  generatePDF() {
    this.spinner.show();
    const isSingle = this.layout === 'single';
    console.log(isSingle);

    const orientation = isSingle ? 'l' : 'p';
    const unit = 'mm';
    const format = isSingle ? [50, 30] : 'a4';

    const doc = new jsPDF(orientation as any, unit, format as any);
    const companyName = this.data.company_name || 'Our Stall';
    const productTitle = this.data.product_title;

    if (isSingle) {
      this.codes.forEach((code, index) => {
        if (index > 0) {
          doc.addPage([50, 30] as any, 'l');
        }

        const w = 44;
        const h = 24;
        const x = 3;
        const y = 3;

        doc.setDrawColor(200, 200, 200);
        doc.setLineDashPattern([1, 1], 0);
        doc.rect(x, y, w, h);
        doc.setLineDashPattern([], 0);

        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(code.serial_number, x + w / 2, y + 4, { align: 'center' });

        if (code.qrDataUrl) {
          doc.addImage(code.qrDataUrl, 'PNG', x + w / 2 - 5.5, y + 6.5, 11, 11);
        }

        doc.setFontSize(5);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'normal');
        doc.text('SCAN TO CLAIM POINTS!', x + w / 2, y + 21, { align: 'center' });
      });
    } else {
      let cols = 3;
      let rows = 8;
      let margin_x = 10;
      let margin_y = 12;
      let spacing_x = 4;
      let spacing_y = 4;
      let w = 58;
      let h = 30;

      if (this.layout === '10x14') {
        cols = 10;
        rows = 14;
        margin_x = 5;
        margin_y = 5;
        spacing_x = 2;
        spacing_y = 2;
        w = 18;
        h = 18;
      } else if (this.layout === '5x10') {
        cols = 5;
        rows = 10;
        margin_x = 8;
        margin_y = 8;
        spacing_x = 3;
        spacing_y = 3;
        w = 35;
        h = 25;
      } else if (this.layout === '2x5') {
        cols = 2;
        rows = 5;
        margin_x = 12;
        margin_y = 15;
        spacing_x = 6;
        spacing_y = 6;
        w = 90;
        h = 48;
      }

      const stickersPerPage = cols * rows;
      const allSlots = [];
      for (let i = 0; i < this.offset; i++) {
        allSlots.push({ isEmpty: true });
      }
      for (const code of this.codes) {
        allSlots.push({ ...code, isEmpty: false });
      }

      let currentIdx = 0;
      let pageNumber = 0;

      while (currentIdx < allSlots.length) {
        if (pageNumber > 0) {
          doc.addPage('a4', 'p');
        }

        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const slotIdx = pageNumber * stickersPerPage + row * cols + col;
            if (slotIdx >= allSlots.length) {
              break;
            }

            const slot = allSlots[slotIdx];
            if (slot.isEmpty) {
              continue;
            }

            const x = margin_x + col * (w + spacing_x);
            const y = margin_y + row * (h + spacing_y);

            doc.setDrawColor(180, 180, 180);
            doc.setLineDashPattern([1, 1], 0);
            doc.rect(x, y, w, h);
            doc.setLineDashPattern([], 0);

            // Set layout-specific sizing
            let fontSize = 9;
            let textYOffset = 4.5;
            let qrSize = 12;
            let qrYOffset = 7.5;
            let ctaFontSize = 6;
            let ctaY = 24.5;

            if (this.layout === '10x14') {
              fontSize = 5;
              textYOffset = 2.5;
              qrSize = 7;
              qrYOffset = 4.5;
              ctaFontSize = 3;
              ctaY = 15;
            } else if (this.layout === '5x10') {
              fontSize = 6;
              textYOffset = 4;
              qrSize = 10;
              qrYOffset = 6;
              ctaFontSize = 4;
              ctaY = 21;
            } else if (this.layout === '2x5') {
              fontSize = 14;
              textYOffset = 7;
              qrSize = 20;
              qrYOffset = 12;
              ctaFontSize = 8;
              ctaY = 40;
            }

            // Draw serial number
            doc.setFontSize(fontSize);
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'bold');
            doc.text(slot.serial_number, x + w / 2, y + textYOffset, { align: 'center' });

            // Draw QR code
            if (slot.qrDataUrl) {
              doc.addImage(slot.qrDataUrl, 'PNG', x + w / 2 - qrSize / 2, y + qrYOffset, qrSize, qrSize);
            }

            // Draw CTA text
            doc.setFontSize(ctaFontSize);
            doc.setTextColor(120, 120, 120);
            doc.setFont('helvetica', 'normal');
            doc.text('SCAN TO CLAIM POINTS!', x + w / 2, y + ctaY, { align: 'center' });
          }
        }

        pageNumber++;
        currentIdx = pageNumber * stickersPerPage;
      }
    }

    const filename = `stickers-${productTitle.replace(/[^a-zA-Z0-9]/g, '_')}-${this.data.batch_id}.pdf`;
    doc.save(filename);
    this.toastr.success('PDF stickers generated successfully!');
    this.spinner.hide();
    this.dialogRef.close();
  }
}